// UK-focused legal drafts for Malek Dridi Hair & Beauty / NøoksPro
// ⚠️ Have a UK solicitor review these before publishing to production.
// All content is presented as JS strings so the same layout can render them.

export const LEGAL_PAGES = {
  privacy: {
    slug: "privacy",
    title: "Privacy Policy",
    subtitle: "How we handle your personal data.",
    updated: "February 2026",
    sections: [
      {
        h: "1. Who we are",
        p: `Malek Dridi Hair & Beauty is a sole-trader business (proprietor: Malek Dridi), trading as NøoksPro, based at 58 Love Street, Paisley, PA3 2DY, Scotland. We are the "data controller" for the information you provide to us. You can contact us any time at hello@malekdridihairandbeauty.com.`,
      },
      {
        h: "2. What we collect",
        p: `We collect the personal data you give us when you book an appointment, buy a product or course, apply for a NøoksPro Trade Account, submit a Colour Match Consultation, sign up to our newsletter or contact us:\n\n• Name, email, phone, delivery/billing address\n• Photos, notes and hair details you share for a colour match\n• Qualification certificates (Trade Account applications only)\n• Payment details (processed directly by Stripe — we never see full card numbers)\n• Basic device/browser data (IP address, cookies) when you visit the site.`,
      },
      {
        h: "3. Why we use it",
        p: `We use your data to:\n• Fulfil your appointments, product orders and course enrolments\n• Send you the emails you'd reasonably expect (order confirmations, download links, colour-match replies, review requests, appointment reminders)\n• Review your Trade Account application\n• Improve our site and services\n• Comply with our legal, accounting and tax obligations.\n\nOur legal bases are your consent (for marketing emails), contract (to fulfil what you've bought), legitimate interests (to run and improve the business) and legal obligation (to keep records HMRC/Companies House requires).`,
      },
      {
        h: "4. Who we share it with",
        p: `We only share your data with the trusted providers we need to run the business:\n• Stripe (payments) — stripe.com/gb/privacy\n• Resend (transactional emails) — resend.com/legal/privacy-policy\n• Ovatu (appointment booking, where applicable)\n• Google (analytics, if you consent)\n• Our accountant / HMRC where legally required.\n\nWe do not sell, rent or trade your personal data to anyone else.`,
      },
      {
        h: "5. How long we keep it",
        p: `We keep booking, order and course-enrolment records for 6 years to meet UK tax and consumer-law obligations. Colour Match photos and Trade Account certificates are kept for as long as your relationship with us is active, plus 12 months. Marketing subscribers stay on the list until they unsubscribe.`,
      },
      {
        h: "6. Your rights",
        p: `Under UK GDPR you can, at any time, ask us to:\n• Give you a copy of what we hold about you\n• Correct anything that's wrong\n• Delete your data (subject to any legal-retention obligations)\n• Stop or restrict how we use it\n• Move it to another provider.\n\nJust email hello@malekdridihairandbeauty.com. You also have the right to complain to the Information Commissioner's Office at ico.org.uk.`,
      },
      {
        h: "7. Cookies",
        p: `We use essential cookies to keep you signed in and process your cart. Analytics cookies (Google) only run if you accept them via our cookie banner. You can change your preference any time by clearing your browser data.`,
      },
      {
        h: "8. Changes",
        p: `We may update this policy occasionally. The "last updated" date at the top will always reflect the current version. Material changes will be highlighted on the site.`,
      },
    ],
  },

  terms: {
    slug: "terms",
    title: "Terms & Conditions",
    subtitle: "The rules for using our site, buying our products and enrolling on our courses.",
    updated: "February 2026",
    sections: [
      {
        h: "1. Who we are & how to contact us",
        p: `This website (malekdridihairandbeauty.com) is operated by Malek Dridi Hair & Beauty, trading as NøoksPro, based at 58 Love Street, Paisley, PA3 2DY, Scotland. You can reach us at hello@malekdridihairandbeauty.com.`,
      },
      {
        h: "2. Placing an order",
        p: `When you submit an order you're making an offer to buy. We accept your offer only once we've emailed you an order confirmation. If any item is unexpectedly out of stock we'll refund you in full within 5 working days. Prices and product descriptions are shown in £ (GBP) and include any applicable VAT.`,
      },
      {
        h: "3. Payment",
        p: `All payments are processed securely by Stripe. Depending on your order and location you may be offered Card, Klarna, Clearpay / Afterpay and Link at checkout. We never see or store your full card details.`,
      },
      {
        h: "4. Delivery",
        p: `Physical items are dispatched from Paisley, Scotland within 3–5 working days of order. Standard UK delivery is £4.95 (free over £50). International delivery is currently unavailable. Once your parcel is with the carrier, delivery times are outside our control but we'll help however we can if anything goes wrong.`,
      },
      {
        h: "5. Digital products",
        p: `Digital products (Hair Extension Essentials Bundle, digital guides) are delivered instantly by secure download link after successful payment. Links stay active for 30 days and allow up to 5 downloads per file. By purchasing a digital product you agree that we start supplying it immediately, which means you waive your 14-day right of cancellation for that item under the Consumer Contracts Regulations 2013.`,
      },
      {
        h: "6. Hair extension products",
        p: `Every extension bundle we sell is genuine 100% Remy human hair. Photos and colour charts are as accurate as we can make them but hair tone can appear different on-screen depending on lighting and your device. For a personal shade recommendation please use our free Colour Match Consultation before ordering.\n\nFor hygiene reasons opened / unpackaged / used hair extensions cannot be returned once the security seal has been broken — this is a statutory exemption under the Consumer Contracts Regulations 2013. See our Returns Policy for full details.`,
      },
      {
        h: "7. Courses & Academy",
        p: `Our Academy courses are run by Malek Dridi Hair & Beauty. See our separate Course Terms & Conditions for enrolment, cancellation, IP and behaviour rules that apply to all students.`,
      },
      {
        h: "8. Trade Accounts",
        p: `Approved trade partners receive an automatic 15% discount on eligible NøoksPro hair extension products. See our separate Trade Account Terms for full eligibility criteria and the discount scope.`,
      },
      {
        h: "9. Bookings and salon services",
        p: `In-salon appointments are booked through Ovatu. All appointments require a 50% booking fee to secure the slot. Cancellations or rescheduling with less than 48 hours' notice forfeit the booking fee.`,
      },
      {
        h: "10. Intellectual property",
        p: `All copy, photographs, video, brand marks and PDF materials on this site remain the property of Malek Dridi Hair & Beauty / NøoksPro. You may not copy, share, sell or redistribute any content, digital download or course material without prior written permission.`,
      },
      {
        h: "11. Limits on our liability",
        p: `Nothing in these Terms limits any legal rights you have as a consumer. We are responsible for foreseeable loss and damage caused by us, but we're not liable for indirect losses or losses that were not foreseeable. Where you buy for business purposes our maximum liability to you is limited to the price you paid for the item in question.`,
      },
      {
        h: "12. Governing law",
        p: `These Terms are governed by the laws of Scotland. Any dispute is subject to the exclusive jurisdiction of the Scottish courts. If you're a consumer resident elsewhere in the UK you retain the benefit of any mandatory local consumer protections.`,
      },
    ],
  },

  returns: {
    slug: "returns",
    title: "Returns & Refund Policy",
    subtitle: "Your right to change your mind — and the hygiene exceptions.",
    updated: "February 2026",
    sections: [
      {
        h: "1. Your 14-day right to change your mind",
        p: `Under the Consumer Contracts Regulations 2013 you have 14 days from the day your order arrives to change your mind on most physical items. To exercise this right, email hello@malekdridihairandbeauty.com with your order number. We'll then have 14 days to refund you once we've received the item back in its original, unopened condition.`,
      },
      {
        h: "2. Items we can't accept back",
        p: `For hygiene and product-integrity reasons we cannot accept returns of:\n• Hair extensions where the security seal has been broken or the hair has been removed from its packaging\n• Any product that has been used, worn, or has come into contact with hair or skin\n• Custom-colour-matched or bespoke items ordered specifically for you\n• Silk scrunchies once removed from their sealed packaging\n• Any digital download (PDF bundles, guides) once the download link has been accessed.\n\nThese exemptions are permitted by regulation 28 of the Consumer Contracts Regulations 2013.`,
      },
      {
        h: "3. Faulty or damaged items",
        p: `If your item arrives damaged, faulty or not as described, please email photographs to hello@malekdridihairandbeauty.com within 30 days of delivery. We'll arrange a full refund, replacement or repair at no cost to you, in line with the Consumer Rights Act 2015.`,
      },
      {
        h: "4. Return postage",
        p: `Where you're returning under your right to change your mind, you pay the return postage. Where an item is faulty or damaged we pay. We recommend a tracked service for anything valuable — we can't refund items lost in transit.`,
      },
      {
        h: "5. Refund method & timing",
        p: `Refunds go back to the original payment method (card, Klarna, Clearpay or Link). Please allow up to 10 working days for the funds to show once we've confirmed the refund.`,
      },
      {
        h: "6. Course refunds",
        p: `See our separate Course Terms for the rules that apply to Academy enrolments. In short: you have 14 days from enrolment to request a refund, provided you haven't started the course.`,
      },
      {
        h: "7. Our return address",
        p: `Malek Dridi Hair & Beauty · Returns · 58 Love Street, Paisley, PA3 2DY, Scotland. Please include your order number inside the parcel.`,
      },
    ],
  },

  "course-terms": {
    slug: "course-terms",
    title: "Course Terms & Conditions",
    subtitle: "Rules for enrolling on any NøoksPro Training Academy course.",
    updated: "February 2026",
    sections: [
      {
        h: "1. Enrolment & payment",
        p: `Your place on a course is confirmed only once payment is received in full. Some courses (marked as such) may accept a deposit — the remaining balance must be paid before the first day.`,
      },
      {
        h: "2. Course access & materials",
        p: `Course fees include tuition, models where applicable, and all NøoksPro course materials (workbook, take-home PDF pack, practical kit where included). Materials are licensed to you for personal educational use only — they may not be copied, resold, shared or used to teach other students.`,
      },
      {
        h: "3. Cancellation by you",
        p: `You have a 14-day right of cancellation from your enrolment date, provided you haven't yet started the course or accessed the digital materials. After that window, or after the course has started, fees are non-refundable except in extraordinary circumstances at Malek's discretion.\n\nYou can transfer your place to another eligible person or to a later course date free of charge with at least 14 days' notice; less than 14 days a £50 admin fee applies.`,
      },
      {
        h: "4. Cancellation by us",
        p: `If we have to cancel a course, we'll offer you a full refund or a place on the next equivalent course, whichever you prefer. We're not liable for consequential losses (travel, accommodation, lost earnings).`,
      },
      {
        h: "5. Your conduct on the day",
        p: `Please arrive on time, prepared and with any pre-course kit requested. We reserve the right to end a session without refund if a student behaves unprofessionally, endangers others or breaches these Terms.`,
      },
      {
        h: "6. Certification",
        p: `On successful completion of a course you'll receive a CPD-accredited NøoksPro certificate. Our courses are CPD accredited, confirming attendance and skill demonstration to a recognised professional development standard.`,
      },
      {
        h: "7. Photos & recording",
        p: `We often photograph and film during courses for our marketing. If you don't want to appear in any content please tell us in writing before the course starts and we'll accommodate. You may not record any part of a course without our written permission.`,
      },
    ],
  },

  "trade-terms": {
    slug: "trade-terms",
    title: "Trade Account Terms",
    subtitle: "The full rules for approved NøoksPro Trade Partners.",
    updated: "February 2026",
    sections: [
      {
        h: "1. Eligibility",
        p: `Trade Accounts are open to professional hairstylists who have completed an accredited hair extension course or hold a recognised industry certificate. We verify every application manually and reserve the right to accept or decline without giving detailed reasons.`,
      },
      {
        h: "2. The 15% Trade Discount",
        p: `Approved trade partners receive an automatic 15% discount at checkout on eligible NøoksPro hair extension products. The discount is applied automatically once you're signed in — no code is required.`,
      },
      {
        h: "3. What the discount does NOT apply to",
        p: `Your trade discount does not apply to, and cannot be combined with:\n• Training courses, masterclasses or any Academy education\n• Gift cards\n• Salon services\n• Any future products explicitly tagged "No Trade Discount"\n• Sale items or promotional codes (unless we tell you otherwise).`,
      },
      {
        h: "4. Resale & re-branding",
        p: `You may use NøoksPro hair on your paying clients within your salon or mobile business. You may not:\n• Repackage or re-brand NøoksPro hair as your own\n• Resell NøoksPro hair online or in retail without our prior written consent\n• Undercut NøoksPro RRP by more than 20% in your own retail pricing.`,
      },
      {
        h: "5. Manual review & revocation",
        p: `We may revoke a Trade Account at any time if we believe it's been misused, if the account holder no longer holds the qualification we relied on, or if the account is used to breach these Terms. In that case any orders in progress are honoured but future discount is disabled.`,
      },
      {
        h: "6. Rest of these Terms",
        p: `All other clauses of our main Terms & Conditions apply to Trade orders. Trade purchases are made on a business-to-business basis and consumer rights under the Consumer Contracts Regulations 2013 do not automatically apply — please check faulty-goods rights under the Sale of Goods Act 1979 as amended.`,
      },
    ],
  },
};
